import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { ModelInfo } from '../types/models';
import { ChevronDown, ChevronUp, AlertTriangle, Scale, Zap, CheckSquare, Layers } from 'lucide-react';

export interface ModelWithProvider extends ModelInfo {
  providerType: string;
}

interface ModelTableDropdownProps {
  models: ModelWithProvider[];
  selectedModelId: string;
  onSelect: (modelId: string, providerType: string) => void;
  disabled?: boolean;
  showCost?: boolean;
  placeholder?: string;
}

type SortColumn = 'model' | 'provider' | 'cost' | 'context' | 'hallucination' | 'legal' | 'performance' | 'instruction';
type SortDirection = 'asc' | 'desc';

function scoreColor(score: number | undefined, invertForRisk = false): string {
  if (score == null) return 'text-gray-400';
  const effective = invertForRisk ? 100 - score : score;
  if (effective >= 80) return 'text-green-600';
  if (effective >= 60) return 'text-yellow-600';
  return 'text-red-600';
}

function formatScore(score: number | undefined): string {
  if (score == null) return '—';
  return score.toFixed(0);
}

function formatCost(cost: number | undefined): string {
  if (cost == null) return '—';
  return `$${cost}`;
}

function formatContextSize(tokens: number | undefined): string {
  if (tokens == null) return '—';
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(1)}M`;
  return `${Math.round(tokens / 1000)}K`;
}

function compareNullable(a: number | undefined, b: number | undefined, dir: SortDirection): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return dir === 'asc' ? a - b : b - a;
}

export const ModelTableDropdown: React.FC<ModelTableDropdownProps> = ({
  models,
  selectedModelId,
  onSelect,
  disabled = false,
  showCost = false,
  placeholder = 'Select Model'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [sortColumn, setSortColumn] = useState<SortColumn>('model');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const containerRef = useRef<HTMLDivElement>(null);
  const tableBodyRef = useRef<HTMLTableSectionElement>(null);
  const selectedRowRef = useRef<HTMLTableRowElement>(null);

  const selectedModel = models.find(m => m.modelId === selectedModelId);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll to selected item when dropdown opens
  useEffect(() => {
    if (isOpen && selectedRowRef.current) {
      selectedRowRef.current.scrollIntoView({ block: 'center', behavior: 'instant' });
    }
  }, [isOpen]);

  const hasBenchmarks = models.some(m =>
    m.hallucinationScore != null || m.legalScore != null ||
    m.performanceScore != null || m.instructionFollowingScore != null
  );

  const handleHeaderClick = useCallback((column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      // Default direction: desc for scores (higher=better), asc for hallucination (lower=better), asc for text
      const defaultDir: SortDirection = column === 'hallucination' ? 'asc' 
        : (column === 'legal' || column === 'performance' || column === 'instruction' || column === 'context') ? 'desc' 
        : 'asc';
      setSortDirection(defaultDir);
    }
  }, [sortColumn]);

  const sortedModels = useMemo(() => {
    const sorted = [...models];
    sorted.sort((a, b) => {
      switch (sortColumn) {
        case 'model':
          return sortDirection === 'asc' 
            ? a.displayName.localeCompare(b.displayName)
            : b.displayName.localeCompare(a.displayName);
        case 'provider':
          return sortDirection === 'asc'
            ? a.providerType.localeCompare(b.providerType)
            : b.providerType.localeCompare(a.providerType);
        case 'cost': {
          const costA = (a.inputTokenCostPer1M ?? 0) + (a.outputTokenCostPer1M ?? 0);
          const costB = (b.inputTokenCostPer1M ?? 0) + (b.outputTokenCostPer1M ?? 0);
          return compareNullable(costA, costB, sortDirection);
        }
        case 'context':
          return compareNullable(a.contextWindowSize, b.contextWindowSize, sortDirection);
        case 'hallucination':
          return compareNullable(a.hallucinationScore, b.hallucinationScore, sortDirection);
        case 'legal':
          return compareNullable(a.legalScore, b.legalScore, sortDirection);
        case 'performance':
          return compareNullable(a.performanceScore, b.performanceScore, sortDirection);
        case 'instruction':
          return compareNullable(a.instructionFollowingScore, b.instructionFollowingScore, sortDirection);
        default:
          return 0;
      }
    });
    return sorted;
  }, [models, sortColumn, sortDirection]);

  const SortIndicator: React.FC<{ column: SortColumn }> = ({ column }) => {
    if (sortColumn !== column) return <ChevronDown className="w-3 h-3 opacity-30" />;
    return sortDirection === 'asc' 
      ? <ChevronUp className="w-3 h-3" /> 
      : <ChevronDown className="w-3 h-3" />;
  };

  return (
    <div data-component="ModelTableDropdown" ref={containerRef} className="relative">
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md bg-white text-left focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
      >
        <span className={selectedModel ? 'text-gray-900' : 'text-gray-500'}>
          {selectedModel 
            ? `${selectedModel.displayName} (${selectedModel.providerType})`
            : placeholder}
        </span>
        <ChevronDown className="w-4 h-4 text-gray-400" />
      </button>

      {/* Dropdown table */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-max min-w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr className="text-left text-xs text-gray-500 uppercase">
                <th 
                  className="px-3 py-2 font-medium cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleHeaderClick('model')}
                >
                  <div className="flex items-center gap-1">
                    <span>Model</span>
                    <SortIndicator column="model" />
                  </div>
                </th>
                <th 
                  className="px-3 py-2 font-medium cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleHeaderClick('provider')}
                >
                  <div className="flex items-center gap-1">
                    <span>Provider</span>
                    <SortIndicator column="provider" />
                  </div>
                </th>
                {showCost && (
                  <th 
                    className="px-3 py-2 font-medium text-center cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleHeaderClick('cost')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      <span>Cost (↑/↓)</span>
                      <SortIndicator column="cost" />
                    </div>
                  </th>
                )}
                {showCost && (
                  <th 
                    className="px-3 py-2 font-medium text-center cursor-pointer hover:bg-gray-100 select-none"
                    title="Context window size in tokens (total input + output capacity)"
                    onClick={() => handleHeaderClick('context')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      <Layers className="w-3 h-3" />
                      <span>Context</span>
                      <SortIndicator column="context" />
                    </div>
                  </th>
                )}
                {hasBenchmarks && (
                  <>
                    <th 
                      className="px-3 py-2 font-medium text-center cursor-pointer hover:bg-gray-100 select-none" 
                      title="Hallucination Risk (lower is better)"
                      onClick={() => handleHeaderClick('hallucination')}
                    >
                      <div className="flex items-center justify-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        <span>Halluc.</span>
                        <SortIndicator column="hallucination" />
                      </div>
                    </th>
                    <th 
                      className="px-3 py-2 font-medium text-center cursor-pointer hover:bg-gray-100 select-none" 
                      title="Legal Domain Score (higher is better)"
                      onClick={() => handleHeaderClick('legal')}
                    >
                      <div className="flex items-center justify-center gap-1">
                        <Scale className="w-3 h-3" />
                        <span>Legal</span>
                        <SortIndicator column="legal" />
                      </div>
                    </th>
                    <th 
                      className="px-3 py-2 font-medium text-center cursor-pointer hover:bg-gray-100 select-none" 
                      title="General Performance (higher is better)"
                      onClick={() => handleHeaderClick('performance')}
                    >
                      <div className="flex items-center justify-center gap-1">
                        <Zap className="w-3 h-3" />
                        <span>Perf.</span>
                        <SortIndicator column="performance" />
                      </div>
                    </th>
                    <th 
                      className="px-3 py-2 font-medium text-center cursor-pointer hover:bg-gray-100 select-none" 
                      title="Instruction Following (higher is better)"
                      onClick={() => handleHeaderClick('instruction')}
                    >
                      <div className="flex items-center justify-center gap-1">
                        <CheckSquare className="w-3 h-3" />
                        <span>Instr.</span>
                        <SortIndicator column="instruction" />
                      </div>
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody ref={tableBodyRef}>
              {sortedModels.map(model => {
                const isSelected = model.modelId === selectedModelId && model.providerType === selectedModel?.providerType;
                return (
                  <tr
                    key={`${model.providerType}-${model.modelId}`}
                    ref={isSelected ? selectedRowRef : undefined}
                    onClick={() => { onSelect(model.modelId, model.providerType); setIsOpen(false); }}
                    className={`cursor-pointer hover:bg-blue-50 border-t border-gray-100 ${
                      isSelected ? 'bg-blue-100' : ''
                    }`}
                  >
                    <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">
                      {model.displayName}
                      {model.isThinkingModel && (
                        <span className="ml-1 text-xs text-purple-600" title="Thinking model">🧠</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                      {model.providerType}
                    </td>
                    {showCost && (
                      <td className="px-3 py-2 text-center text-gray-600 whitespace-nowrap">
                        {model.inputTokenCostPer1M != null || model.outputTokenCostPer1M != null
                          ? `↑${formatCost(model.inputTokenCostPer1M)} ↓${formatCost(model.outputTokenCostPer1M)}`
                          : '—'}
                      </td>
                    )}
                    {showCost && (
                      <td className="px-3 py-2 text-center text-gray-600 whitespace-nowrap"
                          title={model.contextWindowSize != null ? `${model.contextWindowSize.toLocaleString()} tokens` : 'No data'}>
                        {formatContextSize(model.contextWindowSize)}
                      </td>
                    )}
                    {hasBenchmarks && (
                      <>
                        <td className={`px-3 py-2 text-center font-mono ${scoreColor(model.hallucinationScore, true)}`}
                            title={model.hallucinationScore != null ? `Hallucination risk: ${model.hallucinationScore}% (lower is better)` : 'No data'}>
                          {formatScore(model.hallucinationScore)}
                        </td>
                        <td className={`px-3 py-2 text-center font-mono ${scoreColor(model.legalScore)}`}
                            title={model.legalScore != null ? `Legal score: ${model.legalScore}% (higher is better)` : 'No data'}>
                          {formatScore(model.legalScore)}
                        </td>
                        <td className={`px-3 py-2 text-center font-mono ${scoreColor(model.performanceScore)}`}
                            title={model.performanceScore != null ? `Performance: ${model.performanceScore}% (higher is better)` : 'No data'}>
                          {formatScore(model.performanceScore)}
                        </td>
                        <td className={`px-3 py-2 text-center font-mono ${scoreColor(model.instructionFollowingScore)}`}
                            title={model.instructionFollowingScore != null ? `Instruction following: ${model.instructionFollowingScore}% (higher is better)` : 'No data'}>
                          {formatScore(model.instructionFollowingScore)}
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
